import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Sparkles, Trophy, BadgeCheck, Linkedin, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function InitialsAvatar({ name, size = 96 }) {
    const initials = (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
    return (
        <div
            className="flex items-center justify-center rounded-2xl bg-brand-orange/10 font-display text-brand-orange"
            style={{ width: size, height: size, fontSize: size * 0.35 }}
            aria-hidden
        >
            {initials}
        </div>
    );
}

function FormateurCard({ f, idx }) {
    const hasPhoto = !!f.photo_url;
    const isComplete = f.is_complete;
    return (
        <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: (idx % 4) * 0.05 }}
            className="group rounded-2xl border border-brand-line bg-white p-6 shadow-premium transition-all hover:-translate-y-1 hover:shadow-lg"
            data-testid={`formateur-public-card-${idx}`}
        >
            <div className="flex items-start gap-4">
                {hasPhoto ? (
                    <img
                        src={f.photo_url}
                        alt={f.full_name}
                        className="h-24 w-24 flex-shrink-0 rounded-2xl object-cover"
                    />
                ) : (
                    <InitialsAvatar name={f.full_name} size={96} />
                )}
                <div className="min-w-0 flex-1">
                    <h3 className="font-display text-xl leading-tight text-brand-ink">{f.full_name}</h3>
                    {f.professional_title ? (
                        <p className="mt-1 text-xs font-medium uppercase tracking-wider text-brand-orange">
                            {f.professional_title}
                        </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-brand-smoke">
                        {f.years_experience > 0 && <span>{f.years_experience} ans d'expérience</span>}
                        {f.formations_count > 0 && (
                            <span className="flex items-center gap-1">
                                <GraduationCap className="h-3 w-3 text-brand-orange" />
                                {f.formations_count} formation{f.formations_count > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                </div>
                {f.linkedin_url ? (
                    <a
                        href={f.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-shrink-0 rounded-full border border-brand-line p-2 text-brand-smoke transition-colors hover:border-brand-ink hover:text-brand-ink"
                        aria-label={`LinkedIn ${f.full_name}`}
                    >
                        <Linkedin className="h-3.5 w-3.5" />
                    </a>
                ) : null}
            </div>

            <p className="mt-5 line-clamp-4 text-sm text-brand-ink/75">
                {f.short_bio ||
                    "Biographie en cours de rédaction. Ce formateur finalisera son profil prochainement."}
            </p>

            {f.specialties?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {f.specialties.slice(0, 4).map((s) => (
                        <span
                            key={s}
                            className="rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-0.5 text-[10px] font-medium text-brand-ink"
                        >
                            {s}
                        </span>
                    ))}
                </div>
            )}

            {(f.sports_associations?.length > 0 || f.professional_associations?.length > 0) && (
                <div className="mt-4 space-y-1 border-t border-brand-line pt-4 text-[11px] text-brand-smoke">
                    {f.sports_associations?.slice(0, 1).map((a) => (
                        <div key={`s-${a}`} className="flex items-center gap-1.5">
                            <Trophy className="h-3 w-3 flex-shrink-0 text-brand-orange" />
                            <span className="truncate">{a}</span>
                        </div>
                    ))}
                    {f.professional_associations?.slice(0, 1).map((a) => (
                        <div key={`p-${a}`} className="flex items-center gap-1.5">
                            <BadgeCheck className="h-3 w-3 flex-shrink-0 text-brand-orange" />
                            <span className="truncate">{a}</span>
                        </div>
                    ))}
                </div>
            )}

            {!isComplete && (
                <span className="mt-4 inline-block rounded-full border border-brand-line bg-[#FAFAFA] px-2.5 py-0.5 text-[10px] font-medium text-brand-smoke">
                    Profil en cours de finalisation
                </span>
            )}
        </motion.article>
    );
}

export default function NosFormateurs() {
    const [formateurs, setFormateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        document.title = "Nos formateurs · Cursus Physio";
        const meta = document.querySelector('meta[name="description"]') || (() => {
            const m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
            return m;
        })();
        meta.content =
            "Nos formateurs Cursus Physio : kinésithérapeutes experts et praticiens engagés dans la Prévention Active. Découvrez l'équipe pédagogique.";

        api.get("/public/formateurs")
            .then((r) => setFormateurs(r.data || []))
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <main className="bg-white" data-testid="page-nos-formateurs">
            <section className="border-b border-brand-line bg-[#FAFAFA] px-6 py-20 sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        <User className="h-3 w-3" /> Équipe pédagogique
                    </span>
                    <h1 className="mt-6 font-display text-5xl leading-[0.95] text-brand-ink sm:text-6xl lg:text-7xl">
                        Nos <em className="italic text-brand-smoke">formateurs</em>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base text-brand-ink/70 sm:text-lg">
                        Des kinésithérapeutes engagés, des praticiens experts, des intervenants
                        pluridisciplinaires. Tous porteurs de la même conviction : transformer la
                        kinésithérapie par la <strong>Prévention Active</strong>.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
                {loading ? (
                    <div className="py-20 text-center text-sm text-brand-smoke">Chargement…</div>
                ) : err ? (
                    <div className="py-20 text-center text-sm text-red-600">{err}</div>
                ) : (
                    <>
                        <div className="mb-8 flex items-center gap-2 text-sm text-brand-smoke">
                            <Sparkles className="h-4 w-4 text-brand-orange" />
                            <span>
                                {formateurs.length} formateur{formateurs.length > 1 ? "s" : ""} mobilisé
                                {formateurs.length > 1 ? "s" : ""} pour l'Institut
                            </span>
                        </div>
                        <div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="formateurs-grid"
                        >
                            {formateurs.map((f, idx) => (
                                <FormateurCard key={f.id} f={f} idx={idx} />
                            ))}
                        </div>
                    </>
                )}
            </section>
        </main>
    );
}
